import React, { JSX, useEffect, useMemo, useState } from "react";
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  Locale,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { useLanguageStore } from "../stores/languageStore.ts";
import { ChevronLeft, ChevronRight } from "lucide-react";

function getWeekdaysByLocale({
  locale,
  formatStr = "EEEEEE",
}: {
  locale: Locale;
  formatStr?: string;
}) {
  const today = new Date();
  const firstDayOfWeek = startOfWeek(today, { locale });
  const weekdays = [];
  for (let i = 0; i < 7; i++) {
    const day = addDays(firstDayOfWeek, i);
    weekdays.push(format(day, formatStr, { locale }));
  }
  return weekdays;
}

interface MonthCalendarProps {
  availableDates: Set<string>; // set of "yyyy-MM-dd" keys
  onDayClick: (day: Date) => void;
  selectedDay?: Date;
  onlyFutureSlots?: boolean;
  focusMonth?: Date; // when set, calendar navigates to this month
}

const MonthCalendar: React.FC<MonthCalendarProps> = ({
  availableDates,
  onDayClick,
  selectedDay,
  onlyFutureSlots = false,
  focusMonth,
}: MonthCalendarProps) => {
  const { dateFnsLocale } = useLanguageStore();
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const today = startOfDay(new Date());

  useEffect(() => {
    if (focusMonth) {
      setCurrentMonth(startOfMonth(focusMonth));
    }
  }, [focusMonth]);

  const daysOfWeek = useMemo(
    () => getWeekdaysByLocale({ locale: dateFnsLocale }),
    [dateFnsLocale],
  );

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { locale: dateFnsLocale });
  const calendarEnd = endOfWeek(monthEnd, { locale: dateFnsLocale });

  const isPrevDisabled = onlyFutureSlots && isSameMonth(currentMonth, today);

  const days: JSX.Element[] = [];
  let current = calendarStart;

  while (current <= calendarEnd) {
    for (let i = 0; i < 7; i++) {
      const dateStr = format(current, "yyyy-MM-dd");
      const isCurrentMonth = isSameMonth(current, currentMonth);
      const isToday = isSameDay(current, today);
      const past = onlyFutureSlots && isBefore(startOfDay(current), today);
      const freeSlot = availableDates.has(dateStr);
      const isSelected =
        !!selectedDay && isSameDay(selectedDay, current) && !past;

      let circleClasses: string;
      if (past) {
        circleClasses = "text-gray-300 cursor-not-allowed";
      } else if (isSelected) {
        circleClasses = "bg-soluud-primary text-white";
      } else if (isToday) {
        circleClasses =
          "font-bold text-soluud-secondary cursor-pointer hover:bg-soluud-primary-subtle";
      } else if (isCurrentMonth) {
        circleClasses =
          "text-soluud-text-primary cursor-pointer hover:bg-soluud-primary-subtle";
      } else {
        circleClasses = "text-gray-400 cursor-pointer hover:bg-gray-50";
      }

      days.push(
        <div key={dateStr} className="flex flex-col items-center py-0.5">
          <button
            onClick={() => {
              if (!past) onDayClick(new Date(dateStr));
            }}
            disabled={past}
            className={`flex h-9 w-9 items-center justify-center rounded-full text-sm transition-colors ${circleClasses}`}
          >
            {format(current, "d")}
          </button>
          {/* Availability dot — always reserves space to avoid layout shift */}
          <div
            className={`mt-0.5 h-1 w-1 rounded-full ${
              freeSlot && !past && !isSelected
                ? "bg-soluud-primary"
                : "invisible"
            }`}
          />
        </div>,
      );

      current = addDays(current, 1);
    }
  }

  return (
    <div className="w-full select-none">
      {/* Month header */}
      <div className="mb-3 flex items-center justify-between px-1">
        <span className="text-soluud-text-primary text-sm font-semibold">
          {format(currentMonth, "MMMM yyyy", { locale: dateFnsLocale })}
        </span>
        <div className="flex gap-1">
          <button
            onClick={() => {
              if (!isPrevDisabled) setCurrentMonth(subMonths(currentMonth, 1));
            }}
            disabled={isPrevDisabled}
            className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
              isPrevDisabled
                ? "cursor-not-allowed text-gray-300"
                : "text-gray-500 hover:bg-gray-100"
            }`}
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Weekday headers */}
      <div className="mb-1 grid grid-cols-7 text-center">
        {daysOfWeek.map((d) => (
          <div key={d} className="py-1 text-xs font-medium text-gray-400">
            {d}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7">{days}</div>
    </div>
  );
};

export default MonthCalendar;
